import { Button } from '@creditornot/cb-button';
import { InputContainer } from '@creditornot/cb-form';
import { getCookieName } from '@creditornot/cwc-cookie-sdk';
import { FORM_ERROR } from 'final-form';
import React from 'react';
import { Field, Form } from 'react-final-form';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import { useCookie } from 'react-use';
import styled from 'styled-components';

import { patchInboundMerchant } from 'apis/merchant-onboarding-service/api';
import validateYupSchema from 'common/utils/validateYupSchema';
import { ssmoBusinessOpeningTimesInteractionClickComponent } from 'core/telemetry/avo/Avo';
import { useRegisterTelemetryEvent } from 'core/telemetry/hooks/useRegisterTelemetryEvent';
import { useCookieConsents } from 'modules/cookie-consents/CookieConsentsProvider';
import WeeklyScheduleInput from 'modules/self-serve-sign-up/common/WeeklyScheduleInput/WeeklyScheduleInput';
import { DEFAULT_WEEKLY_SCHEDULE } from 'modules/self-serve-sign-up/common/WeeklyScheduleInput/constants';

import { SsmoStepsIdentifiers } from '../../constants';
import { InboundMerchant } from '../../types';
import { apiWeeklyScheduleToOpenningHours, openingHoursToApi } from '../transformer';
import { OpeningHours, OpeningHoursValidationSchema } from '../validation';

type OpeningTimesPageProps = {
  inboundMerchant: InboundMerchant;
};

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledButton = styled(Button).attrs({
  size: 'large',
})`
  flex-grow: 1;
  margin-block-start: 56px;
`;

const OpeningTimesForm = ({ inboundMerchant }: OpeningTimesPageProps) => {
  const { formatMessage } = useIntl();
  const history = useHistory();

  const register = useRegisterTelemetryEvent();
  const [updateIsInProgress, setUpdateIsInProgress] = React.useState(false);

  const {
    consents: { analytics: analyticsConsent, marketing: marketingConsent },
  } = useCookieConsents();

  const [woltUid] = useCookie(getCookieName('wolt-uid'));

  const { country } = inboundMerchant;

  const weeklySchedule =
    inboundMerchant.venues.length && inboundMerchant.venues[0].openingTimes
      ? apiWeeklyScheduleToOpenningHours(inboundMerchant.venues[0].openingTimes)
      : { openingHours: DEFAULT_WEEKLY_SCHEDULE };

  const handleOpeningHoursSubmit = async (values: OpeningHours): Promise<{ [key: string]: string | Error } | void> => {
    try {
      setUpdateIsInProgress(false);
      const validFormValues = OpeningHoursValidationSchema.validateSync(values);
      const stepParam = SsmoStepsIdentifiers.OpeningHours;
      const transformedValues = {
        businessType: 'restaurant',
        venues: [
          {
            ...inboundMerchant.venues[0],
            productLine: 'restaurant',
            openingTimes: openingHoursToApi(validFormValues),
          },
        ],
      };

      ssmoBusinessOpeningTimesInteractionClickComponent({ clickTarget: 'save' });

      register({
        event: 'venue_opening_hours_submitted',
        marketingConsent,
        analyticsConsent,
        properties: {
          __woltUid: woltUid,
          country,
        },
      });

      await patchInboundMerchant({ ...transformedValues, ssmoStepCompletionId: stepParam }, inboundMerchant.merchantId);

      history.push('/onboarding-page');
    } catch (submitError) {
      return { [FORM_ERROR]: submitError as Error };
    } finally {
      setUpdateIsInProgress(false);
    }
  };

  return (
    <Form<OpeningHours>
      initialValues={weeklySchedule}
      onSubmit={handleOpeningHoursSubmit}
      validate={validateYupSchema(OpeningHoursValidationSchema)}
    >
      {({ handleSubmit }) => (
        <StyledForm onSubmit={handleSubmit} id={SsmoStepsIdentifiers.OpeningHours}>
          <InputContainer label={formatMessage({ id: 'self-serve-sign-up.form-labels.venues.days-open' })}>
            <Field name="openingHours">
              {({ input }) => <WeeklyScheduleInput value={input.value} onChange={input.onChange} />}
            </Field>
          </InputContainer>

          <StyledButton type="submit" variant="primary" loading={updateIsInProgress}>
            {formatMessage({ id: 'state.save' })}
          </StyledButton>
        </StyledForm>
      )}
    </Form>
  );
};

export default OpeningTimesForm;
